﻿import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useCallback } from 'react';
import CopyToClipboardButton from '../../components/common/CopyToClipboardButton';
import { RefreshOutline } from '../../icons/icons';
import { NotamSearchResultItem, NotamService } from '../../services/axios/axios-api-v2-generated';
import { useEvent } from '../contexts/event-bus';
import { LoadingIndicatorLinear } from './indicators/LoadingIndicatorLinear';

export function NotamWindow() {
    const [notamString, setNotamString] = React.useState<string | null>(null);

    useEvent(
        'show-notam-window',
        useCallback(({ notam }) => setNotamString(notam), [])
    );

    const { data, isLoading, isSuccess, error, refetch } = useQuery<NotamSearchResultItem[], AxiosError<{ traceId?: string }>>({
        queryKey: ['notam', notamString],
        queryFn: () => NotamService.searchAsync({ query: notamString! }, { withAuthorization: true }),
        enabled: !!notamString,
        retry: false
    });

    if (notamString) {
        return (
            <Dialog title="NOTAM details" minWidth={'min(80vw, 450px)'} onClose={() => setNotamString(null)} contentStyle={{ padding: 0 }}>
                <div
                    className="px-3 pb-3"
                    style={{ maxWidth: 'min(820px, 80vw)', maxHeight: 'min(820px, 80vh)', overflowX: 'hidden', overflowY: 'auto' }}>
                    {isLoading && <LoadingIndicatorLinear />}
                    {isSuccess && (
                        <>
                            <div>
                                {data.map((x) => (
                                    <NotamDetails key={x.id} raw={x.rawText ?? ''} />
                                ))}
                            </div>
                            {data.length === 0 && <div className="text-danger">NOTAM not found.</div>}
                        </>
                    )}
                    {error && (
                        <div className="text-danger">
                            <div>Failed to load NOTAM.</div>
                            <Button className="mt-2" svgIcon={RefreshOutline} onClick={() => refetch()}>
                                Try again
                            </Button>
                            {/* Here we can show trace ID to make it easier for us to track error from customers */}
                            {/* {error.response && error.response?.data?.traceId && (
                                <div style={{ fontSize: '.8rem' }}>Trace ID: {error.response.data.traceId}</div>
                            )} */}
                        </div>
                    )}
                </div>
            </Dialog>
        );
    }
}

function NotamDetails(props: { raw: string }) {
    const idx = props.raw.indexOf(' ');

    const content =
        idx > 0 ? (
            <>
                <div className="d-flex gap-2">
                    <div className="font-mono" style={{ whiteSpace: 'pre', lineBreak: 'auto', textWrap: 'wrap', flexShrink: 0 }}>
                        {props.raw.slice(0, idx)}
                    </div>
                    <div className="font-mono" style={{ whiteSpace: 'pre', lineBreak: 'auto', textWrap: 'wrap' }}>
                        {props.raw.slice(idx + 1)}
                    </div>
                </div>
            </>
        ) : (
            <div className="font-mono" style={{ whiteSpace: 'pre', lineBreak: 'anywhere', textWrap: 'wrap' }}>
                {props.raw}
            </div>
        );
    return (
        <div className="d-flex flex-column align-items-end">
            <CopyToClipboardButton getText={() => props.raw} className="mb-2" />
            {content}
        </div>
    );
}
